/**
 * EPC Contract master readiness (Draft → submit gate).
 * Authority remains BE submit/activate; FE uses this for UX only.
 */

export type ContractReadinessState = 'READY' | 'MISSING' | 'WARNING';

export type ContractReadinessCheck = {
  id: string;
  label: string;
  state: ContractReadinessState;
  detail?: string;
  blocking: boolean;
};

export type ContractReadinessResult = {
  checks: ContractReadinessCheck[];
  overall: ContractReadinessState;
  canSubmit: boolean;
  submitBlockedReason?: string;
};

function parseMeta(raw: unknown): Record<string, unknown> {
  if (!raw) return {};
  if (typeof raw === 'object' && !Array.isArray(raw)) return raw as Record<string, unknown>;
  if (typeof raw === 'string' && raw.trim()) {
    try {
      const p = JSON.parse(raw) as unknown;
      if (p && typeof p === 'object' && !Array.isArray(p)) return p as Record<string, unknown>;
    } catch {
      return {};
    }
  }
  return {};
}

function asStr(v: unknown): string {
  return v == null ? '' : String(v).trim();
}

function asNum(v: unknown): number | null {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

export function buildContractMasterReadiness(input: {
  contract: Record<string, unknown>;
  particulars?: Record<string, unknown> | null;
  clauseProfile?: string | null;
}): ContractReadinessResult {
  const c = input.contract;
  const p = { ...parseMeta(c.particulars_json || c.particulars), ...(input.particulars || {}) };
  const profile = asStr(input.clauseProfile || c.clause_profile || c.clauseProfile || 'CUSTOM');

  const checks: ContractReadinessCheck[] = [];

  const title = asStr(c.title);
  checks.push({
    id: 'title',
    label: 'Contract title',
    state: title ? 'READY' : 'MISSING',
    blocking: true,
    detail: title || 'Required for execution package identity',
  });

  const value = asNum(c.contract_value ?? c.contractValue);
  checks.push({
    id: 'value',
    label: 'Contract value',
    state: value != null && value > 0 ? 'READY' : 'MISSING',
    blocking: true,
    detail: value != null && value > 0 ? undefined : 'Positive contract value required',
  });

  const model = asStr(c.commercial_model || c.commercialModel);
  checks.push({
    id: 'model',
    label: 'Commercial model',
    state: model ? 'READY' : 'MISSING',
    blocking: true,
    detail: model || 'e.g. Lump Sum / Remeasurement / Cost Plus',
  });

  const customer = asStr(c.customer_name || c.customerName);
  checks.push({
    id: 'customer',
    label: 'Customer / employer',
    state: customer ? 'READY' : 'MISSING',
    blocking: true,
  });

  checks.push({
    id: 'clause_profile',
    label: 'Clause profile (FIDIC / standard)',
    state: profile ? 'READY' : 'MISSING',
    blocking: true,
    detail: profile || 'Select FIDIC or company standard',
  });

  const commencement = asStr(p.commencement_date || p.commencementDate);
  checks.push({
    id: 'commencement',
    label: 'Commencement date',
    state: commencement ? 'READY' : 'MISSING',
    blocking: true,
  });

  const completion = asStr(p.completion_date || p.completionDate || p.original_completion_date);
  checks.push({
    id: 'completion',
    label: 'Completion date',
    state: completion ? 'READY' : 'MISSING',
    blocking: true,
  });

  const paymentTerms = asStr(p.payment_terms || p.paymentTerms);
  checks.push({
    id: 'payment_terms',
    label: 'Payment terms',
    state: paymentTerms ? 'READY' : 'MISSING',
    blocking: true,
    detail: paymentTerms || 'e.g. 30 days from IPC certification',
  });

  const retention = asNum(p.retention_pct ?? p.retentionPct);
  checks.push({
    id: 'retention',
    label: 'Retention %',
    state: retention != null && retention >= 0 ? 'READY' : 'MISSING',
    blocking: true,
  });

  const ld = asNum(p.ld_daily_rate ?? p.ldDailyRate ?? p.liquidated_damages_daily);
  checks.push({
    id: 'ld',
    label: 'LD daily rate',
    state: ld != null && ld >= 0 ? 'READY' : 'WARNING',
    blocking: false,
    detail: ld != null ? undefined : 'Recommended for large EPC awards',
  });

  const perfBond = asNum(p.performance_bond_pct ?? p.performanceBondPct);
  checks.push({
    id: 'perf_bond',
    label: 'Performance bond %',
    state: perfBond != null && perfBond > 0 ? 'READY' : 'WARNING',
    blocking: false,
    detail: perfBond != null ? undefined : 'Typical 5–10% for contractor securities',
  });

  const advance = asNum(p.advance_payment_pct ?? p.advancePaymentPct);
  checks.push({
    id: 'advance',
    label: 'Advance payment %',
    state: advance != null ? 'READY' : 'WARNING',
    blocking: false,
  });

  const missing = checks.filter((x) => x.blocking && x.state === 'MISSING');
  const warnings = checks.filter((x) => x.state === 'WARNING');
  const canSubmit = missing.length === 0;
  const overall: ContractReadinessState = !canSubmit
    ? 'MISSING'
    : warnings.length
      ? 'WARNING'
      : 'READY';

  return {
    checks,
    overall,
    canSubmit,
    submitBlockedReason: canSubmit
      ? undefined
      : `Complete: ${missing.map((m) => m.label).join(', ')}`,
  };
}

export function contractReadinessTagColor(state: ContractReadinessState): string {
  if (state === 'READY') return 'success';
  if (state === 'WARNING') return 'warning';
  return 'error';
}
